import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, ArrowLeft, Phone } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingActions from "./FloatingActions";
import CookieBanner from "./CookieBanner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { services, contactInfo } from "../mock";
import { serviceSeo } from "../serviceSeo";

const ServiceIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon className={className} strokeWidth={2.1} />;
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = services.find((s) => s.slug === slug);
  const seo = serviceSeo[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title =
        (seo && seo.metaTitle) ||
        `${service.title} Augsburg | MAG Facility Services UG`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute(
          "content",
          (seo && seo.metaDescription) ||
            `${service.title} in Augsburg & Umgebung von MAG Facility Services UG – ${service.short} Jetzt kostenloses Angebot anfordern.`
        );
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", `https://www.mag-facilityservices.de/leistungen/${slug}`);
    }

    // Strukturierte Daten (JSON-LD) für SEO: Service, Breadcrumb & FAQ
    const scripts = [];
    const addLd = (obj, marker) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(marker, "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      scripts.push(s);
    };

    if (service) {
      const url = `https://www.mag-facilityservices.de/leistungen/${slug}`;
      addLd(
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.title,
          name: `${service.title} Augsburg`,
          description: (seo && seo.metaDescription) || service.short,
          url,
          provider: {
            "@type": "ProfessionalService",
            name: "MAG Facility Services UG",
            telephone: "+49 155 62974055",
            areaServed: [
              "Augsburg",
              "Gersthofen",
              "Neusäß",
              "Friedberg",
              "Königsbrunn",
              "Stadtbergen",
              "Bobingen",
            ],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Augsburg",
              postalCode: "86154",
              addressCountry: "DE",
            },
          },
          areaServed: {
            "@type": "City",
            name: "Augsburg",
          },
        },
        "data-service-ld"
      );

      addLd(
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Startseite",
              item: "https://www.mag-facilityservices.de/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Leistungen",
              item: "https://www.mag-facilityservices.de/#leistungen",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: service.title,
              item: url,
            },
          ],
        },
        "data-breadcrumb-ld"
      );
    }

    if (service && seo && seo.faqs && seo.faqs.length) {
      addLd(
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: seo.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        "data-service-faq"
      );
    }

    return () => {
      document.title = "MAG Facility Services UG | Gebäudereinigung Augsburg";
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", "https://www.mag-facilityservices.de/");
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [service, seo, slug]);

  useEffect(() => {
    if (!service) navigate("/");
  }, [service, navigate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  if (!service) return null;

  const relatedSlugs = (seo && seo.related) || [];
  const related = relatedSlugs.length
    ? relatedSlugs
        .map((rs) => services.find((s) => s.slug === rs))
        .filter(Boolean)
    : services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-[74px] bg-white overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-0 w-[720px] h-[720px] rounded-full bg-blue-100/60 blur-3xl" />
            <div className="absolute top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-50 blur-3xl" />
          </div>
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
            <Link to="/#leistungen" className="inline-flex items-center gap-2 text-[14px] font-medium text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Alle Leistungen
            </Link>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 text-white shrink-0">
                <ServiceIcon name={service.icon} className="w-7 h-7" />
              </div>
              <span className="text-[13px] font-bold tracking-[0.18em] text-blue-600 uppercase">
                Gebäudereinigung Augsburg
              </span>
            </div>
            <h1 className="font-display mt-5 text-slate-900 text-[34px] sm:text-[50px] leading-[1.05] tracking-tight" style={{ fontWeight: 800 }}>
              {service.title}
            </h1>
            <p className="mt-4 text-[17px] text-slate-600 max-w-2xl leading-relaxed">
              {service.short}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/#kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 text-white text-[15px] font-semibold hover:bg-blue-700 transition-colors">
                Kostenloses Angebot anfordern <ArrowRight className="w-4 h-4" />
              </a>
              <a href={contactInfo.phoneHref} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-[15px] font-semibold hover:border-slate-400 hover:bg-slate-50 transition-colors">
                <Phone className="w-4 h-4" /> {contactInfo.phone}
              </a>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-5">
              <h2 className="font-display text-[26px] text-slate-900" style={{ fontWeight: 800 }}>
                {service.title} vom Profi in Augsburg
              </h2>
              <p className="text-[16px] leading-relaxed text-slate-600">{service.details}</p>
              <p className="text-[16px] leading-relaxed text-slate-600">{service.long}</p>

              {seo && seo.sections && seo.sections.map((sec) => (
                <div key={sec.heading} className="reveal pt-4">
                  <h3 className="font-display text-[19px] text-slate-900 mb-3" style={{ fontWeight: 700 }}>
                    {sec.heading}
                  </h3>
                  {sec.paragraphs.map((p, i) => (
                    <p key={i} className="text-[16px] leading-relaxed text-slate-600 mb-3">
                      {p}
                    </p>
                  ))}
                </div>
              ))}

              <div className="reveal pt-4">
                <h3 className="font-display text-[19px] text-slate-900 mb-4" style={{ fontWeight: 700 }}>
                  Das ist inklusive
                </h3>
                <ul className="space-y-3">
                  {service.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 shrink-0 mt-0.5">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {related.length > 0 && (
                <div className="reveal pt-4 rounded-xl bg-blue-50/60 border border-blue-100 p-5">
                  <h3 className="font-display text-[16px] text-slate-900 mb-2" style={{ fontWeight: 700 }}>
                    Passende Leistungen für Ihr Objekt
                  </h3>
                  <p className="text-[15px] leading-relaxed text-slate-600">
                    Kombinieren Sie {service.title} ideal mit unseren weiteren Leistungen:{" "}
                    {related.map((r, i) => (
                      <span key={r.slug}>
                        <Link
                          to={`/leistungen/${r.slug}`}
                          className="text-blue-600 font-medium hover:text-blue-700 underline decoration-blue-200 underline-offset-2"
                          data-testid={`inline-related-${r.slug}`}
                        >
                          {r.title}
                        </Link>
                        {i < related.length - 1 ? ", " : "."}
                      </span>
                    ))}
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar CTA */}
            <aside className="lg:col-span-1">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:sticky lg:top-24">
                <h3 className="font-display text-[19px] text-slate-900" style={{ fontWeight: 700 }}>
                  Jetzt Angebot sichern
                </h3>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                  Wir melden uns innerhalb von 24 Stunden mit einem individuellen,
                  transparenten Angebot – kostenlos & unverbindlich.
                </p>
                <a href="/#kontakt" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
                  Angebot anfordern <ArrowRight className="w-4 h-4" />
                </a>
                <a href={contactInfo.phoneHref} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-semibold hover:bg-white transition-colors">
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* FAQ */}
        {seo && seo.faqs && seo.faqs.length > 0 && (
          <section className="py-16 lg:py-20 bg-white border-t border-slate-100" data-testid="service-faq-section">
            <div className="reveal max-w-3xl mx-auto px-5 sm:px-8">
              <span className="text-[13px] font-bold tracking-[0.18em] text-blue-600 uppercase">
                Häufige Fragen
              </span>
              <h2 className="font-display mt-3 text-slate-900 text-[26px] sm:text-[32px]" style={{ fontWeight: 800 }}>
                FAQ zur {service.title} in Augsburg
              </h2>
              <Accordion type="single" collapsible className="mt-8">
                {seo.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200" data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-[16px] font-semibold text-slate-900 hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-slate-600 pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* Related */}
        <section className="py-16 bg-slate-50">
          <div className="reveal max-w-5xl mx-auto px-5 sm:px-8">
            <h2 className="font-display text-[24px] text-slate-900 mb-8" style={{ fontWeight: 800 }}>
              Weitere Leistungen
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((s) => (
                <Link
                  key={s.id}
                  to={`/leistungen/${s.slug}`}
                  className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <ServiceIcon name={s.icon} className="w-6 h-6" />
                  </div>
                  <h3 className="font-display mt-4 text-[17px] text-slate-900" style={{ fontWeight: 700 }}>
                    {s.title}
                  </h3>
                  <p className="mt-2 text-[14px] text-slate-600 leading-relaxed line-clamp-2">{s.short}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingActions />
      <CookieBanner />
    </>
  );
};

export default ServiceDetail;
